import axios from 'axios';
import { API_BASE_URL } from '../constants/api';

const API_BASE = `${API_BASE_URL}/call-analytics`;

export const callAnalyticsAPI = {
  // Import endpoints
  importRingCentral: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await axios.post(`${API_BASE}/import-ringcentral`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    // Backend returns { success: true, message: "...", results: {...} }
    return response.data.results || response.data;
  },

  importGoogleAds: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await axios.post(`${API_BASE}/import-google-ads`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    // Backend returns { success: true, message: "...", results: {...} }
    return response.data.results || response.data;
  },

  // Campaign configuration endpoints
  getCampaigns: async () => {
    const response = await axios.get(`${API_BASE}/campaigns`);
    return response.data.data || response.data;
  },

  createCampaign: async (campaignData) => {
    const response = await axios.post(`${API_BASE}/campaigns`, campaignData);
    return response.data;
  },

  updateCampaign: async (id, campaignData) => {
    const response = await axios.put(`${API_BASE}/campaigns/${id}`, campaignData);
    return response.data;
  },

  deleteCampaign: async (id) => {
    const response = await axios.delete(`${API_BASE}/campaigns/${id}`);
    return response.data;
  },

  // Analytics endpoints
  getDailyStats: async (params) => {
    const response = await axios.get(`${API_BASE}/daily-stats`, { params });
    return response.data.data || response.data;
  },

  aggregateData: async (params) => {
    const response = await axios.post(`${API_BASE}/aggregate`, params);
    return response.data;
  },

  getSummary: async (params) => {
    const response = await axios.get(`${API_BASE}/summary`, { params });
    return response.data.data || response.data;
  },

  // Recording endpoints
  uploadRecordings: async (files) => {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });
    
    const response = await axios.post(`${API_BASE}/recordings/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  getRecordings: async (params) => {
    const response = await axios.get(`${API_BASE}/recordings`, { params });
    // Backend returns { success: true, recordings: [...], pagination: {...} }
    return response.data;
  },

  getRecording: async (id) => {
    const response = await axios.get(`${API_BASE}/recordings/${id}`);
    // Backend returns { success: true, recording: {...} }
    return response.data.recording || response.data;
  },

  updateRecording: async (id, data) => {
    const response = await axios.put(`${API_BASE}/recordings/${id}`, data);
    return response.data;
  },

  deleteRecording: async (id) => {
    const response = await axios.delete(`${API_BASE}/recordings/${id}`);
    return response.data;
  },

  bulkDeleteRecordings: async (ids) => {
    const response = await axios.post(`${API_BASE}/recordings/bulk-delete`, { ids });
    return response.data;
  },

  bulkAssignCampaign: async (recordingIds, campaignName) => {
    const response = await axios.post(`${API_BASE}/recordings/bulk-assign-campaign`, { 
      callIds: recordingIds, 
      campaignName 
    });
    return response.data;
  },

  // Transcription endpoints
  transcribeRecording: async (id, language = 'en-US', useQueue = true) => {
    const response = await axios.post(`${API_BASE}/recordings/${id}/transcribe`, { language, useQueue });
    return response.data;
  },

  batchTranscribe: async (recordingIds) => {
    const response = await axios.post(`${API_BASE}/recordings/batch-transcribe`, { recordingIds });
    return response.data;
  },

  estimateTranscriptionCost: async (recordingIds) => {
    const response = await axios.post(`${API_BASE}/transcription/estimate-cost`, { recordingIds });
    return response.data;
  },

  getJobStatus: async (jobId) => {
    const response = await axios.get(`${API_BASE}/transcription/job/${jobId}`);
    return response.data;
  },

  cancelJob: async (jobId) => {
    const response = await axios.delete(`${API_BASE}/transcription/job/${jobId}`);
    return response.data;
  },

  getQueueStats: async () => {
    const response = await axios.get(`${API_BASE}/transcription/queue/stats`);
    return response.data;
  },

  pauseQueue: async () => {
    const response = await axios.post(`${API_BASE}/transcription/queue/pause`);
    return response.data;
  },

  resumeQueue: async () => {
    const response = await axios.post(`${API_BASE}/transcription/queue/resume`);
    return response.data;
  },

  // Data deletion endpoints
  deleteRingCentralData: async () => {
    const response = await axios.delete(`${API_BASE}/ringcentral-calls`);
    return response.data;
  },

  deleteGoogleAdsData: async () => {
    const response = await axios.delete(`${API_BASE}/google-ads-calls`);
    return response.data;
  },

  deleteAllCallAnalytics: async () => {
    const response = await axios.delete(`${API_BASE}/all-call-analytics`);
    return response.data;
  },

  // Grouped analytics (combines multiple campaigns by city/phone)
  aggregateGroupedData: async (params) => {
    const response = await axios.post(`${API_BASE}/aggregate-grouped`, params);
    return response.data;
  },

  // Category management endpoints
  getCategories: async (filters = {}) => {
    const response = await axios.get(`${API_BASE}/categories`, { params: filters });
    return response.data.categories || response.data;
  },

  getCategory: async (id) => {
    const response = await axios.get(`${API_BASE}/categories/${id}`);
    return response.data.category || response.data;
  },

  createCategory: async (categoryData) => {
    const response = await axios.post(`${API_BASE}/categories`, categoryData);
    return response.data;
  },

  updateCategory: async (id, categoryData) => {
    const response = await axios.put(`${API_BASE}/categories/${id}`, categoryData);
    return response.data;
  },

  deleteCategory: async (id) => {
    const response = await axios.delete(`${API_BASE}/categories/${id}`);
    return response.data;
  },

  getCategoryStats: async () => {
    const response = await axios.get(`${API_BASE}/categories/stats`);
    return response.data;
  },

  // AI Categorization endpoints
  categorizeRecording: async (id, force = false) => {
    const response = await axios.post(`${API_BASE}/recordings/${id}/categorize`, { force });
    return response.data;
  },

  bulkCategorize: async (recordingIds = null) => {
    const response = await axios.post(`${API_BASE}/recordings/bulk-categorize`, { recordingIds });
    return response.data;
  },

  confirmCategory: async (id, userId = null) => {
    const response = await axios.post(`${API_BASE}/recordings/${id}/confirm-category`, { userId });
    return response.data;
  },

  rejectCategory: async (id, customCategory, userId = null) => {
    const response = await axios.post(`${API_BASE}/recordings/${id}/reject-category`, { 
      customCategory, 
      userId 
    });
    return response.data;
  },

  getCategorizationStats: async () => {
    const response = await axios.get(`${API_BASE}/categorization/stats`);
    return response.data;
  },

  // Speaker identification endpoints
  identifySpeakers: async (id) => {
    const response = await axios.post(`${API_BASE}/recordings/${id}/identify-speakers`);
    return response.data;
  },

  batchIdentifySpeakers: async (recordingIds = null) => {
    const response = await axios.post(`${API_BASE}/recordings/batch-identify-speakers`, { recordingIds });
    return response.data;
  },

  getFormattedTranscript: async (id) => {
    const response = await axios.get(`${API_BASE}/recordings/${id}/transcript-formatted`);
    return response.data;
  }
};
